<template>
  <div :class="store.sidebarClass">
    <div class="content-with-spacing">
      <div v-if="ready && meetingForm" id="meeting-form-page">
        <section class="heading-container dashboard">
          <div class="heading-content">
            <div class="title-container">
              <div class="meeting-title-status">
                <h1>{{ meetingForm.name }}</h1>
                <span v-if="meetingMarkedComplete" class="meeting-completed">
                  <i class="fas fa-check" aria-hidden="true"></i> Meeting Completed
                </span>
              </div>
              <span v-if="meetingStart && meetingEnd" class="meeting-date-time">
                {{parseTimestamp(meetingStart, undefined, "do MMM yyyy")}}, {{parseTimestamp(meetingStart, undefined, "HH:mm")}} - {{parseTimestamp(meetingEnd, undefined, "HH:mm")}}
              </span>
              <div v-if="!requiresLearnerSignature" class="no-learner-signature-note">
                <i class="fas fa-info-circle" aria-hidden="true"></i> This meeting does not require a learner signature.
              </div>
              <mark-complete-manual-override v-if="latestSubmission && !meetingHasLearnerSignature && $permissions.hasPermission('mark_meeting_complete_without_learner_signature')"
                :learner-id="parseInt(studentId, 10)"
                :module-meeting-id="moduleMeetingId"
                :is-complete="meetingMarkedComplete"
                @update="getLatestSubmission"
              ></mark-complete-manual-override>
              <div v-if="$permissions.hasPermission('view_user_meetings_hidden_from_employer')" class="share-with-employer-status">
                <p>
                  <i :class="meetingForm.share_with_employer ? 'fas fa-eye' : 'fas fa-eye-slash'" aria-hidden="true"></i>
                  {{ employerAccessMessage }}
                </p>
                <button v-if="meetingForm.share_with_employer"
                        class="btn btn-warning"
                        @click="hideFromEmployer">Hide from Employer
                        <k-loader v-if="isHiddenFromEmployerLoading" :size="20"></k-loader>
                    </button>
                <button v-if="!meetingForm.share_with_employer"
                        class="btn btn-success"
                        @click="shareWithEmployer">Make Visible to Employer
                      <k-loader v-if="isHiddenFromEmployerLoading" :size="20"></k-loader>
                    </button>
              </div>
              <p class="description" v-html="getMarkdown(meetingForm.description)"></p>
            </div>
            <div class="heading-details">
              <p v-if="latestSubmission" class="submission-date">Last submission: <b>{{parseTimestamp(latestSubmission.date_timestamp)}}</b></p>
            </div>
            <div class="profile-cards-container">
              <div v-if="meetingOwner" class="profile-card meeting-owner-profile">
                <user-profile-card
                  :name="meetingOwner.user_name"
                  :role="meetingOwner.user_role"
                  :email="meetingOwner.email"
                ></user-profile-card>
              </div>
              <div v-if="studentData" class="profile-card student-profile">
                <user-profile-card
                  :name="studentData.full_name"
                  :role="'Learner'"
                  :avatarUrl="studentData.avatar_url"
                  :email="studentData.email"
                ></user-profile-card>
              </div>
            </div>
            <learner-rag
              v-if="$featureFlags.isEnabled('meeting-form-and-learner-profile-rag') && $permissions.hasPermission('view_user_rag_summary') && parsedStudentId && programmeId"
              class="meeting-rag-panel"
              :student-id="parsedStudentId"
              :programme-id="programmeId"
            />
          </div>
        </section>
        <section>
          <k-meeting-form
            ref="meetingForm"
            :questionnaire="meetingForm"
            :latest-submission="latestSubmission"
            :read-only="readOnlyFormElements"
            :disable-text-toolbar="false"
            >
            <template v-if="isMeetingOwner && !meetingMarkedComplete" #actions>
              <div class="button-container">
                <button @click.prevent="clear" class="btn btn-secondary reset-button" type="button" title="Reset responses">Reset</button>
                <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmit">Submit Responses</button>
              </div>
            </template>
            <template v-if="isMeetingOwner && !meetingMarkedComplete" #bottom-actions>
              <meeting-form-declaration v-model="declarationChecked" :show-signing-button="false"></meeting-form-declaration>
              <div class="submit-form-button bottom-submit-button">
                <button @click.prevent="submit" class="btn btn-primary submit-button" :disabled="disableSubmit">Submit Responses</button>
                <button @click.prevent="saveDraft" class="btn btn-default save-draft-button">Save as Draft</button>
              </div>
            </template>
          </k-meeting-form>
          <div v-if="isMeetingOwner && declarationChecked" class="employer-sharing-disclaimer">
            <p>
              <i class="fas fa-info-circle" aria-hidden="true"></i> After submitting your notes for the meeting, they will become available to view by the learner and their Line Manager.
              If you need to make these notes unavailable to a learner's Line Manager for any reason, please use the
              <span class="hide-from-employer-text"><b> Hide from Employer</b></span> button at the top of the page after submitting.
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style>
.share-with-employer-status button img {
  filter: brightness(0);
}
</style>

<style scoped>
.heading-content {
  width: 100%;
}

.meeting-title-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.meeting-completed {
  color: var(--kate-type-success) !important;
  font-size: 2em;
}

.user-profile-card {
  margin: 10px 0;
}

#content #dashboard-landing h1 {
  padding-left: 0;
  margin-bottom: 5px;
}

.share-with-employer-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 15px 0;
}

.share-with-employer-status p {
  font-style: italic;
  font-weight: bolder;
  margin: 0;
  color: var(--kate-warning) !important;
}

[data-theme="light"] .share-with-employer-status p {
  color: var(--kate-type-warning-dark) !important;
}

.share-with-employer-status button {
  float: right;
  display: flex;
  align-items: center;
  gap: 5px;
}

.hide-from-employer-text {
  color: var(--kate-warning);
}

[data-theme="light"] .hide-from-employer-text {
  color: var(--kate-type-warning-dark);
}

.button-container {
  display: flex;
  justify-content: right;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 15px;
}

.submit-form-button {
  position: relative;
  display: inline-block;
}

.submit-form-button button {
  padding: 10px 25px;
}

.bottom-submit-button {
  gap: 10px;
  display: flex;
  justify-content: center;
  width: 100%;
}

.employer-sharing-disclaimer {
  background-color: var(--kate-panel-alt);
  border: 1px solid var(--kate-stroke-dark);
  border-radius: 15px;
  padding: 8px;
  margin-top: 15px;
}

.profile-cards-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 15px;
}

.profile-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.profile-card :deep(.user-profile-card) {
  flex: 1;
  height: 100%;
  margin: 0;
  box-sizing: border-box;
  align-items: flex-start;
}

.meeting-rag-panel {
  margin-top: 15px;
}

.meeting-date-time {
  display: block;
  font-size: 1.17em;
  font-weight: bold;
  color: var(--kate-text-primary, inherit);
}

.no-learner-signature-note {
  display: inline-block;
  align-self: flex-start;
  max-width: 100%;
  font-style: italic;
  font-weight: bold;
  background-color: var(--kate-panel-alt);
  border: 1px solid var(--kate-stroke-dark);
  border-radius: 15px;
  padding: 8px;
  margin-top: 15px;
}

/* Side by side layout on wider screens */
@media (min-width: 768px) {
  .profile-cards-container {
    flex-direction: row;
    gap: 20px;
    align-items: stretch;
  }
}
</style>

<script>
import useGlobalStore from '@stores/global';
import TimeMixins from '@mixins/time-mixins';
import { markdown } from '@utils/maths-markdown';
import KMeetingForm from '../../../../questionnaires/k-questionnaire.vue';
import UserProfileCard from '../../../../components/user-profile-card.vue';
import MeetingFormDeclaration from '../../../../questionnaires/meetings/meeting-form-declaration.vue';
import MarkCompleteManualOverride from '../../../../questionnaires/meetings/mark-complete-manual-override.vue';
import KLoader from '../../../../components/k-loader.vue';
import LearnerRag from '../../../student/learner-rag.vue';

export default {
  mixins: [TimeMixins],

  components: {
    UserProfileCard,
    KMeetingForm,
    MeetingFormDeclaration,
    MarkCompleteManualOverride,
    KLoader,
    LearnerRag,
  },

  props: {
    from: {
      type: String,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
      meetingFormReady: false,
      meetingForm: undefined,
      latestSubmissionReady: false,
      latestSubmission: undefined,
      isSubmitting: false,
      disableSubmission: true,
      declarationChecked: false,
      studentData: undefined,
      studentDataReady: false,
      meetingOwner: undefined,
      meetingStart: undefined,
      meetingEnd: undefined,
      requiresLearnerSignature: true,
      isHiddenFromEmployerLoading: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getMeetingForm();
    if (this.studentId) {
      this.getStudentData();
    }
  },

  watch: {
    ready(val) {
      if (val) {
        this.registerCrumbs();
        this.$Loading.finish();
        this.$nextTick(() => {
          this.disableSubmission = false;
        });
      } else {
        this.$Loading.start();
      }
    },
    isSubmitting(val) {
      if (val) {
        this.$Loading.minimal();
      } else {
        this.$Loading.finish();
      }
    },
    latestSubmission() {
      this.declarationChecked = this.latestSubmissionSignedByCurrentUser;
    },
    studentId(val) {
      if (val) {
        this.getStudentData();
      }
    },
  },

  computed: {
    ready() {
      return this.meetingFormReady && this.latestSubmissionReady && this.studentDataReady && !this.isSubmitting;
    },
    moduleMeetingId() {
      return parseInt(this.$route.params.moduleMeetingId, 10);
    },
    studentId() {
      return this.$route.params.studentId;
    },
    parsedStudentId() {
      return parseInt(this.studentId, 10);
    },
    programmeId() {
      if (this.meetingForm?.programme_id == null) {
        return undefined;
      }
      return parseInt(this.meetingForm.programme_id, 10);
    },
    userModuleMeetingId() {
      return this.meetingForm.user_module_meeting_id;
    },
    meetingFormEndpoint() {
      return `/api/curriculum/admin/meeting/${this.moduleMeetingId}/users/${this.studentId}/form`;
    },
    getSubmissionEndpoint() {
      const urlArg = this.isMeetingOwner ? '' : '?is_draft=false';
      return `/api/curriculum/meeting/${this.moduleMeetingId}/user/${this.studentId}/submission${urlArg}`;
    },
    employerAccessMessage() {
      if (this.meetingForm.share_with_employer) {
        return 'The notes from this meeting are visible to the learner\'s employer';
      }
      return 'The notes from this meeting are hidden from the learner\'s employer';
    },
    latestSubmissionSignedByCurrentUser() {
      if (!this.latestSubmission || !Array.isArray(this.latestSubmission.signatures)) {
        return false;
      }
      return this.latestSubmission.signatures.some(sig => sig?.signee_user_id === parseInt(this.store.userId, 10));
    },
    isMeetingOwner() {
      if (!this.meetingOwner || !this.store.userId) {
        return false;
      }
      return parseInt(this.meetingOwner.user_id, 10) === parseInt(this.store.userId, 10);
    },
    readOnlyFormElements() {
      if (this.meetingMarkedComplete) {
        return true;
      }
      return !this.isMeetingOwner;
    },
    disableSubmit() {
      // Submission is disabled if the meeting form has not been signed by the user or the latest submission has not been signed by the user
      return !(this.declarationChecked || this.latestSubmissionSignedByCurrentUser);
    },
    meetingMarkedComplete() {
      return Boolean(this.latestSubmission?.completed_on);
    },
    meetingHasLearnerSignature() {
      if (!this.latestSubmission || !Array.isArray(this.latestSubmission.signatures)) {
        return false;
      }
      return this.latestSubmission.signatures.some(sig => sig?.signee_user_id === parseInt(this.studentId, 10));
    },
  },

  methods: {
    submit() {
      const payload = this.$refs.meetingForm.getSubmitPayload();
      payload.is_draft = false;
      this.submitAndSignMeetingForm(payload);
    },
    saveDraft() {
      const payload = this.$refs.meetingForm.getSubmitPayload();
      payload.is_draft = true;
      this.submitAndSignMeetingForm(payload);
    },
    clear() {
      this.$refs.meetingForm.clearResponses();
    },
    registerCrumbs() {
      let crumbs;
      if (this.from === 'learner_profile') {
        // Came from learner profile page
        crumbs = [
          {
            text: this.studentData.full_name,
            path: {
              name: 'dashboard_students',
              params: { studentId: this.studentId },
            },
          },
          { text: this.meetingForm.name, active: true },
        ];
      } else {
        // Came from meetings inbox
        crumbs = [
          {
            text: 'Meetings Inbox',
            path: {
              name: 'meetings_inbox',
            },
          },
          { text: this.meetingForm.name, active: true },
        ];
      }
      this.$crumbs.register(crumbs);
    },

    signingEndpoint(submissionId) {
      return `/api/curriculum/admin/meeting/${this.moduleMeetingId}/user/${this.studentId}/submission/${submissionId}/signature`;
    },

    getMeetingForm() {
      this.$logger.info('Getting meeting form info', { moduleMeetingId: this.moduleMeetingId }, true);
      this.$http.get(this.meetingFormEndpoint).then(response => {
        this.$logger.info('Got meeting form info', { moduleMeetingId: this.moduleMeetingId });
        this.meetingForm = response.data;
        this.meetingOwner = this.meetingForm.meeting_owner;
        this.meetingStart = this.meetingForm.meeting_start;
        this.meetingEnd = this.meetingForm.meeting_end;
        this.requiresLearnerSignature = this.meetingForm.requires_learner_signature;
        this.meetingFormReady = true;
        this.getLatestSubmission();
      }).catch(err => {
        if (err.response && err.response.status === 403) {
          // This happens when the user doesn't have access to the meeting form (e.g. hidden from employer)
          this.$router.push({ name: '403' });
        }
        if (this.$http.isWarning(err)) {
          this.$logger.warn('Meeting form not found', { moduleMeetingId: this.moduleMeetingId }, err);
        } else {
          this.$logger.error('Error getting meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
          this.$ktoast.showError(err, true);
        }
      });
    },

    getLatestSubmission() {
      this.latestSubmissionReady = false;
      this.$logger.info('Getting latest meeting form submission', { moduleMeetingId: this.moduleMeetingId }, true);
      this.$http.get(this.getSubmissionEndpoint).then(response => {
        this.$logger.info('Got latest meeting form submission', { moduleMeetingId: this.moduleMeetingId });
        this.latestSubmission = response.data;
        this.previousResponses = this.latestSubmission?.responses;
      }).catch(err => {
        if (this.$http.isWarning(err) || err.response.status === 403) {
          this.$router.push({ name: '403' });
          this.$logger.warn('Latest meeting form submission not found', { moduleMeetingId: this.moduleMeetingId }, err);
        } else {
          this.$logger.autowarn('Error getting latest meeting form submission', { moduleMeetingId: this.moduleMeetingId }, err);
          this.$ktoast.showError(err, true);
        }
      }).then(() => {
        this.latestSubmissionReady = true;
      });
    },

    submitMeetingFormRequest(payload) {
      if (this.disableSubmission) {
        return Promise.resolve();
      }
      this.$logger.info('Submitting meeting form', { moduleMeetingId: this.moduleMeetingId }, true);
      return this.$http.post(`/api/curriculum/meeting/${this.moduleMeetingId}/user/${this.studentId}/submission`, payload)
        .then(response => {
          if (payload.is_draft) {
            this.$logger.info('Draft submission, skipping signature', { moduleMeetingId: this.moduleMeetingId });
            return Promise.resolve();
          }
          this.$logger.info('Meeting form submitted', { moduleMeetingId: this.moduleMeetingId });
          return response;
        })
        .catch(err => {
          this.$logger.autowarn('Error submitting meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
          throw err;
        });
    },

    signatureRequest(submissionId) {
      this.$logger.info('Signing meeting form', { moduleMeetingId: this.moduleMeetingId }, true);
      return this.$http.post(this.signingEndpoint(submissionId))
        .then(() => {
          this.$logger.info('Meeting form signed', { moduleMeetingId: this.moduleMeetingId });
        })
        .catch(err => {
          this.$logger.autowarn('Error signing meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
          throw err;
        });
    },

    async submitAndSignMeetingForm(payload) {
      this.isSubmitting = true;

      try {
        this.$logger.info('Submitting and signing meeting form', { moduleMeetingId: this.moduleMeetingId }, true);
        const submissionResponse = await this.submitMeetingFormRequest(payload);
        if (!payload.is_draft) {
          await this.signatureRequest(submissionResponse.data.id);
        }
        this.$ktoast.success('Meeting updated');
        this.getLatestSubmission();
      } catch (err) {
        this.$logger.error('Error submitting and signing meeting form', { moduleMeetingId: this.moduleMeetingId }, err);
        this.$ktoast.showError(err);
      } finally {
        this.isSubmitting = false;
      }
    },

    getMarkdown(str) {
      return markdown(str);
    },

    getStudentData() {
      this.studentDataReady = false;
      this.$logger.info('Getting student profile', { studentId: this.studentId }, true);
      this.$http.get(`/api/profile/users/${this.studentId}`)
        .then(result => {
          this.studentData = result.data;
          this.$logger.info('Got student profile', { studentId: this.studentId });
        }).catch(error => {
          this.$logger.info('Error getting student profile', { studentId: this.studentId }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.studentDataReady = true;
        });
    },
    hideFromEmployer() {
      this.isHiddenFromEmployerLoading = true;
      this.$logger.info('Hiding form data from employer');
      this.$http.put(`/api/curriculum/admin/user/meeting/${this.userModuleMeetingId}/hide-from-employer`)
        .then(() => {
          this.meetingForm.share_with_employer = false;
          this.$logger.info('Form data hidden from employer', { userModuleMeetingId: this.userModuleMeetingId });
        }).catch(error => {
          this.$logger.autowarn('Error hiding form data from employer', { userModuleMeetingId: this.userModuleMeetingId }, error);
          this.$ktoast.showError(error);
        }).finally(() => {
          this.isHiddenFromEmployerLoading = false;
        });
    },
    shareWithEmployer() {
      this.isHiddenFromEmployerLoading = true;
      this.$logger.info('Sharing form data from employer');
      this.$http.put(`/api/curriculum/admin/user/meeting/${this.userModuleMeetingId}/share-with-employer`)
        .then(() => {
          this.meetingForm.share_with_employer = true;
          this.$logger.info('Form data shared with employer', { userModuleMeetingId: this.userModuleMeetingId });
        }).catch(error => {
          this.$logger.autowarn('Error sharing form data with employer', { userModuleMeetingId: this.userModuleMeetingId }, error);
          this.$ktoast.showError(error);
        }).finally(() => {
          this.isHiddenFromEmployerLoading = false;
        });
    },
  },
};
</script>
